import { motion, useMotionValue, animate, useInView } from "framer-motion";
import { useEffect, useRef, useState } from "react";

function Counter({ to, prefix = "", suffix = "", decimals = 0 }) {
  const [display, setDisplay] = useState("0");
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const mv = useMotionValue(0);

  useEffect(() => {
    if (!inView) return;
    const controls = animate(mv, to, {
      duration: 2.2,
      ease: [0.22, 1, 0.36, 1],
      onUpdate: (v) => {
        setDisplay(v.toLocaleString("en-IN", { maximumFractionDigits: decimals, minimumFractionDigits: decimals }));
      },
    });
    return () => controls.stop();
  }, [inView, to, decimals, mv]);

  return (
    <span ref={ref} className="tabular">
      {prefix}
      {display}
      {suffix}
    </span>
  );
}

const items = [
  { pre: "₹", val: 3200, post: "+ Cr", label: "CAPEX Governed" },
  { val: 15, post: "", label: "Programs Commissioned" },
  { val: 21, post: "+", label: "Years of Practice" },
  { val: 364, post: " days", label: "Pearl Project — commissioned within" },
];

export default function Achievements() {
  return (
    <section id="achievements" className="relative border-b border-surface-2">
      <div className="mx-auto max-w-[1440px] px-6 md:px-16 py-28 md:py-40">
        <div className="text-[11px] uppercase tracking-[0.28em] text-brass mb-10">
          <span className="tabular">09 —</span><span className="ml-3">Signature Achievements</span>
        </div>
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="font-serif-display font-light text-ivory text-5xl md:text-7xl leading-[0.95] tracking-[-0.02em] max-w-4xl"
        >
          What has been delivered, <span className="italic text-brass">measured in cash and calendar</span>.
        </motion.h2>

        <div className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-px bg-surface-2">
          {items.map((it, i) => (
            <div key={i} className="bg-ink p-8 md:p-12">
              <div className="font-serif-display text-ivory text-5xl md:text-7xl font-light leading-none">
                <Counter to={it.val} prefix={it.pre} suffix={it.post} />
              </div>
              <div className="mt-6 text-[11px] uppercase tracking-[0.22em] text-mutedfg leading-relaxed">
                {it.label}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16">
          <motion.div
            initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="border border-surface-2 p-10"
          >
            <div className="text-brass text-[11px] uppercase tracking-[0.22em] mb-4">Award</div>
            <h3 className="font-serif-display text-ivory text-2xl md:text-3xl font-light leading-tight">
              Team Excellence Award — WHRS Program
            </h3>
            <p className="mt-4 text-mutedfg leading-relaxed font-light">
              Three boilers, STG, ACC and BOP delivered in a record 11 months, without cost overrun.
            </p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            transition={{ duration: 0.9, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            className="border border-surface-2 p-10"
          >
            <div className="text-brass text-[11px] uppercase tracking-[0.22em] mb-4">Operational Impact</div>
            <h3 className="font-serif-display text-ivory text-2xl md:text-3xl font-light leading-tight">
              Cumulative savings of ~₹11.5 Cr across three programs.
            </h3>
            <p className="mt-4 text-mutedfg leading-relaxed font-light">
              Delivered through constraint management, procurement discipline and interface risk resolution.
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
